// Gemeinsamer Mail-Entwurf: bevorzugt als .eml-Datei (HTML + eingebettetes
// Firmenlogo), die Outlook dank "X-Unsent: 1" als sendefertigen Entwurf öffnet.
// mailto: bleibt als logoloser Fallback — Versand passiert bewusst in Outlook.

import { useState } from 'react';
import { LOGO_PNG_B64 } from './logoSickinger';

export type MailDraft = { an: string; betreff: string; text: string };
// Dateianhang für den .eml-Entwurf (Inhalt Base64, z. B. ein erzeugtes PDF)
export type MailAnhang = { name: string; mime: string; b64: string };

// Offizielle Signatur für alle Einkauf-Mails (Jasmin Sickinger)
export const MAIL_SIGNATUR =
  'Mit freundlichen Grüßen\n' +
  'Jasmin Sickinger\n' +
  'Einkauf / technische Sachbearbeitung\n' +
  '\n' +
  'Manfred Sickinger GmbH & Co. KG\n' +
  'Mönchswiesen 12\n' +
  '71735 Eberdingen\n' +
  'GERMANY\n' +
  '\n' +
  'Tel.:  +49 7042/96820122\n' +
  'Fax.: +49 7042/7033\n' +
  'E-Mail: j.sickinger@apparatebau-sickinger.de\n' +
  'Homepage: www.apparatebau-sickinger.de\n' +
  '\n' +
  'Unsere Datenschutzhinweise finden Sie unter:\n' +
  'www.apparatebau-sickinger.de/datenschutz.html\n' +
  '\n' +
  '\n' +
  'Geschäftsführer: Ralph und Uwe Sickinger\n' +
  'Reg.-Gericht: HRA 290141 Amtsgericht Stuttgart\n' +
  'Ust-Id.Nr. DE145002701';

export function mailtoLink(d: MailDraft): string {
  return `mailto:${encodeURIComponent(d.an)}?subject=${encodeURIComponent(d.betreff)}&body=${encodeURIComponent(d.text)}`;
}

function b64utf8(s: string): string {
  return btoa(String.fromCharCode(...new TextEncoder().encode(s)));
}
function mit76Zeichen(b64: string): string {
  return b64.replace(/(.{76})/g, '$1\r\n');
}
function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// .eml-Entwurf (multipart/related): HTML-Text + Logo als Inline-Bild (CID).
// "X-Unsent: 1" lässt Outlook die Datei im Verfassen-Modus öffnen.
// Zeilenumbrüche als echte <br> — Outlook rendert mit der Word-Engine
// und ignoriert white-space:pre-wrap (Text klebte sonst in einem Absatz).
export function emlEntwurf(d: MailDraft, anhaenge: MailAnhang[] = []): Blob {
  const html =
    '<html><body>' +
    `<div style="font-family:Calibri,Arial,sans-serif;font-size:11pt;">${escapeHtml(d.text).replace(/\n/g, '<br>\r\n')}</div>` +
    '<br><img src="cid:mslogo" width="300" alt="Manfred Sickinger GmbH &amp; Co. KG" style="display:block;">' +
    '</body></html>';
  // Mit Anhängen: multipart/mixed um den bisherigen multipart/related-Teil (Text + Logo)
  const kopf = [
    `To: ${d.an}`,
    `Subject: =?utf-8?B?${b64utf8(d.betreff)}?=`,
    'X-Unsent: 1',
    'MIME-Version: 1.0',
  ];
  const mitAnhang = anhaenge.length > 0;
  const eml = [
    ...kopf,
    ...(mitAnhang
      ? ['Content-Type: multipart/mixed; boundary="MS-AUSSEN"', '', '--MS-AUSSEN']
      : []),
    'Content-Type: multipart/related; boundary="MS-GRENZE"',
    '',
    '--MS-GRENZE',
    'Content-Type: text/html; charset=utf-8',
    'Content-Transfer-Encoding: base64',
    '',
    mit76Zeichen(b64utf8(html)),
    '--MS-GRENZE',
    'Content-Type: image/png; name="logo.png"',
    'Content-Transfer-Encoding: base64',
    'Content-ID: <mslogo>',
    'Content-Disposition: inline; filename="logo.png"',
    '',
    mit76Zeichen(LOGO_PNG_B64),
    '--MS-GRENZE--',
    ...anhaenge.flatMap((x) => [
      '',
      '--MS-AUSSEN',
      `Content-Type: ${x.mime}; name="${x.name}"`,
      'Content-Transfer-Encoding: base64',
      `Content-Disposition: attachment; filename="${x.name}"`,
      '',
      mit76Zeichen(x.b64),
    ]),
    ...(mitAnhang ? ['', '--MS-AUSSEN--'] : []),
  ].join('\r\n');
  return new Blob([eml], { type: 'message/rfc822' });
}

// Druckansicht im offiziellen Angebots-Briefbogen (identisch zur Kalkulation-
// Vorlage): CI-Kopf mit Logo + Firmenblock, Empfänger, Nr-/Datum-Box, blauer
// Titel, Fußzeile mit Bank-/Gerichtsangaben. Nutzer wählt „Als PDF speichern".
export function druckeAlsPdf(d: MailDraft): void {
  const w = window.open('', '_blank', 'width=820,height=1000');
  if (!w) { alert('Popup blockiert — bitte Popups erlauben.'); return; }
  const datum = new Date().toLocaleDateString('de-DE');
  w.document.write(
    '<!DOCTYPE html><html lang="de"><head><meta charset="utf-8"><title>' + escapeHtml(d.betreff || 'Anfrage') + '</title>' +
    '<style>' +
    '@page{size:A4;margin:0}*{box-sizing:border-box}' +
    'body{font-family:Calibri,Arial,sans-serif;font-size:11pt;color:#111;margin:0}' +
    '.seite{width:210mm;min-height:297mm;padding:16mm 15mm 14mm;position:relative}' +
    '.kopf{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px}' +
    '.kopf img{height:56px;width:auto;object-fit:contain}' +
    '.fadr{text-align:right;font-size:8.5pt;color:#555;line-height:1.4}.fadr b{color:#1B5E9E;font-size:9pt}' +
    '.zeile{display:flex;justify-content:space-between;align-items:flex-start;gap:24px;margin-bottom:20px}' +
    '.absender{font-size:8pt;color:#94a3b8;border-bottom:1px solid #cbd5e1;padding-bottom:2px;margin-bottom:8px}' +
    '.empf{font-weight:bold;font-size:11.5pt}' +
    '.nrbox{border:1px solid #cbd5e1;border-collapse:collapse;font-size:9pt}' +
    '.nrbox td{padding:4px 12px;border-bottom:1px solid #e2e8f0}.nrbox .lbl{color:#64748b;background:#f8fafc}' +
    'h1.titel{color:#1B5E9E;font-size:16pt;margin:4px 0 12px;font-weight:bold}' +
    '.txt{white-space:pre-wrap;font-size:11pt;line-height:1.5;color:#1e293b}' +
    '.fuss{position:absolute;left:15mm;right:15mm;bottom:11mm;border-top:2px solid #1B5E9E;padding-top:7px;' +
    'display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;font-size:7.5pt;color:#64748b;line-height:1.45}' +
    '.fuss b{color:#334155}' +
    '</style></head><body>' +
    '<div class="seite">' +
      '<div class="kopf">' +
        '<img src="data:image/png;base64,' + LOGO_PNG_B64 + '" alt="Manfred Sickinger GmbH & Co. KG">' +
        '<div class="fadr"><b>Manfred Sickinger GmbH &amp; Co.KG</b><br>' +
          'Mönchswiesen 12 · 71735 Eberdingen<br>' +
          'Tel.: 07042 - 7098 · Fax: 07042 - 7033<br>' +
          'info@apparatebau-sickinger.de<br>www.Apparatebau-Sickinger.de</div>' +
      '</div>' +
      '<div class="zeile">' +
        '<div><div class="absender">Manfred Sickinger GmbH &amp; Co.KG · Mönchswiesen 12 · 71735 Eberdingen</div>' +
          '<div class="empf">' + escapeHtml(d.an || '—') + '</div></div>' +
        '<table class="nrbox"><tr><td class="lbl">Datum</td><td>' + datum + '</td></tr>' +
          '<tr><td class="lbl">Seite</td><td>1</td></tr></table>' +
      '</div>' +
      '<h1 class="titel">' + escapeHtml(d.betreff || 'Anfrage') + '</h1>' +
      '<div class="txt">' + escapeHtml(d.text) + '</div>' +
      '<div class="fuss">' +
        '<div><b>Manfred Sickinger GmbH &amp; Co.KG</b><br>Mönchswiesen 12<br>71735 Eberdingen<br>' +
          'Erfüllungsort u. Gerichtsstand für beide Teile: Stuttgart</div>' +
        '<div>Tel.: 07042 - 7098<br>Fax: 07042 - 7033<br>info@apparatebau-sickinger.de<br>' +
          'www.Apparatebau-Sickinger.de<br>USt-Id.Nr. DE145002701</div>' +
        '<div><b>KSK Ludwigsburg</b><br>IBAN DE71 6045 0050 0009 8049 84<br>BIC SOLADES1LBG<br>' +
          'Geschäftsführer: Uwe Sickinger · Ralph Sickinger</div>' +
      '</div>' +
    '</div>' +
    '<script>window.onload=function(){window.print();}<\/script></body></html>'
  );
  w.document.close();
}

// Entwurf herunterladen — ein Klick auf die Datei öffnet sie in Outlook.
// Dateiname strikt ASCII: Nicht-ASCII im download-Attribut lässt Chromium
// auf "download" (ohne .eml!) zurückfallen, dann öffnet Outlook nicht.
export function oeffneOutlookEntwurf(d: MailDraft, anhaenge: MailAnhang[] = []): void {
  const ascii = d.betreff
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
    .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue').replace(/ß/g, 'ss')
    .replace(/[^A-Za-z0-9 ._-]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 60);
  const url = URL.createObjectURL(emlEntwurf(d, anhaenge));
  const a = document.createElement('a');
  a.href = url;
  a.download = (ascii || 'Mail-Entwurf') + '.eml';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// Erzeugtes PDF: Mail-Anhang + Funktion zum direkten Speichern (Download)
export type MailPdf = { anhang: MailAnhang; speichern: () => void };

// pdf (optional): erzeugt das PDF erst beim Klick (aktueller Stand). Dann hängt der
// Outlook-Entwurf das PDF an; „Als PDF speichern“ lädt es nur herunter.
export function MailModal({ draft, onChange, onClose, titel = '✉ Mail an Lieferant',
  hinweisOhneEmail = 'Keine E-Mail im Lieferantenstamm gefunden — bitte eintragen.', pdf }: {
  draft: MailDraft; onChange: (d: MailDraft) => void; onClose: () => void;
  titel?: string; hinweisOhneEmail?: string; pdf?: () => Promise<MailPdf>;
}) {
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfFehler, setPdfFehler] = useState('');
  async function mitPdf(aktion: (p: MailPdf) => void) {
    setPdfBusy(true); setPdfFehler('');
    try { aktion(await pdf!()); }
    catch (e) { setPdfFehler((e as Error).message || 'PDF konnte nicht erzeugt werden.'); }
    setPdfBusy(false);
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">{titel}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>
        <div className="p-6 space-y-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">An</label>
            <input className="input w-full" value={draft.an} placeholder="E-Mail des Lieferanten"
                   onChange={e => onChange({ ...draft, an: e.target.value })} />
            {!draft.an && <div className="text-xs text-amber-600 mt-1">{hinweisOhneEmail}</div>}
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Betreff</label>
            <input className="input w-full" value={draft.betreff}
                   onChange={e => onChange({ ...draft, betreff: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Text</label>
            <textarea className="input w-full" rows={10} value={draft.text}
                      onChange={e => onChange({ ...draft, text: e.target.value })} />
          </div>
        </div>
        {pdfFehler && <div className="px-6 pb-2 text-sm text-red-600" data-testid="mail-pdf-fehler">{pdfFehler}</div>}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
          {!pdf && (
            <a href={mailtoLink(draft)} className="text-sm text-slate-500 hover:underline mr-auto">
              ohne Logo (mailto)
            </a>
          )}
          <button className={`btn-secondary${pdf ? ' mr-auto' : ''}`}
                  onClick={() => { navigator.clipboard?.writeText(`An: ${draft.an}\nBetreff: ${draft.betreff}\n\n${draft.text}`); }}>
            Text kopieren
          </button>
          {pdf ? (
            <>
              <button className="btn-secondary" disabled={pdfBusy} data-testid="mail-pdf-speichern"
                      onClick={() => mitPdf(p => p.speichern())} title="Bestellung als PDF herunterladen">
                ⬇ PDF speichern
              </button>
              <button disabled={pdfBusy} data-testid="mail-outlook-pdf"
                      onClick={() => mitPdf(p => oeffneOutlookEntwurf(draft, [p.anhang]))}
                      className="bg-brand text-white rounded-lg px-5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50">
                {pdfBusy ? 'Erzeugt PDF …' : '📧 Outlook-Entwurf mit PDF'}
              </button>
            </>
          ) : (
            <>
              <button className="btn-secondary" onClick={() => druckeAlsPdf(draft)} title="Druckansicht mit Briefkopf — im Dialog „Als PDF speichern“ wählen">
                🖨 PDF
              </button>
              <button onClick={() => oeffneOutlookEntwurf(draft)}
                      className="bg-brand text-white rounded-lg px-5 py-2 text-sm font-medium hover:opacity-90">
                📧 Outlook-Entwurf (mit Logo)
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
